import { IconBoltOutline18, IconCodeBranchOutline18, IconLayersStackedOutline18, IconPlusOutline18 } from "nucleo-ui-outline-18";
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, type Edge, type EdgeProps } from "@xyflow/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { NodeExecutionStatus } from "@/types/runStream";
import React, { memo, useCallback, useMemo, useState } from "react";
export interface WorkflowEdgeData extends Record<string, unknown> {
  onAddActionBetween?: (sourceId: string, targetId: string) => void;
  onAddLogicBetween?: (sourceId: string, targetId: string) => void;
  onAddBlockBetween?: (sourceId: string, targetId: string) => void;
  onDropBetween?: (sourceId: string, targetId: string, dragData: any) => void;
  hideAddButton?: boolean;
  /** Execution status of the source node - used for dynamic edge styling */
  sourceNodeStatus?: NodeExecutionStatus;
  /** Execution status of the target node - used for branch edges to detect un-taken paths */
  targetNodeStatus?: NodeExecutionStatus;
  /** Whether there's an active run */
  hasActiveRun?: boolean;
  /**
   * Whether the user is currently dragging an action/logic node from the sidebar library.
   * Managed at WorkflowCanvas level (single listener) and passed via edge.data to avoid
   * per-edge document event listeners (which caused 3×N listeners for N edges).
   */
  isDraggingFromLibrary?: boolean;
  /** Whether this edge connects a for-each node to its onComplete (post-loop) node */
  isOnComplete?: boolean;
  /** Whether the target node is a for-each node (edge line hidden, drawn by ForEachLoopBoundary) */
  isTargetForEach?: boolean;
}
type WorkflowEdge = Edge<WorkflowEdgeData>;
function WorkflowEdgeComponent({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  source,
  target,
  data,
  sourceHandle
}: EdgeProps<WorkflowEdge> & {
  sourceHandle?: string | null;
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // isDraggingFromLibrary is passed via edge.data from WorkflowCanvas (single canvas-level
  // listener) instead of registering document event listeners in each edge instance.
  const isGlobalDragging = !!data?.isDraggingFromLibrary;
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition
  });

  // onComplete edges and edges targeting for-each nodes are visually
  // represented by ForEachLoopBoundary (custom SVG lines + "+" buttons).
  // Hide the React Flow edge entirely to avoid duplicate rendering.
  if (data?.isOnComplete || data?.isTargetForEach) {
    return <BaseEdge id={id} path={edgePath} style={{
      stroke: "none"
    }} />;
  }

  // Don't show + button if target is a placeholder node (redundant), if hideAddButton is true,
  // or while a workflow run is active — inserting nodes mid-run would diverge the running graph
  // from the snapshot the executor is iterating through (B3-4064).
  const isTargetPlaceholder = target.startsWith("placeholder-");
  const hasEditableControls = !!(data?.onAddActionBetween || data?.onAddLogicBetween || data?.onAddBlockBetween || data?.onDropBetween);
  const shouldHideButton = isTargetPlaceholder || data?.hideAddButton || data?.isOnComplete || data?.hasActiveRun || !hasEditableControls;
  const handleAddAction = useCallback(() => {
    setMenuOpen(false);
    if (data?.onAddActionBetween) {
      data.onAddActionBetween(source, target);
    }
  }, [data, source, target]);
  const handleAddLogic = useCallback(() => {
    setMenuOpen(false);
    if (data?.onAddLogicBetween) {
      data.onAddLogicBetween(source, target);
    }
  }, [data, source, target]);
  const handleAddBlock = useCallback(() => {
    setMenuOpen(false);
    if (data?.onAddBlockBetween) {
      data.onAddBlockBetween(source, target);
    }
  }, [data, source, target]);
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if we're dragging from React Flow (contains the right data type)
    if (e.dataTransfer.types.includes("application/reactflow")) {
      setIsDragOver(true);
    }
  }, []);
  const handleDragLeave = useCallback((e_0: React.DragEvent) => {
    e_0.preventDefault();
    e_0.stopPropagation();
    setIsDragOver(false);
  }, []);
  const handleDrop = useCallback((e_1: React.DragEvent) => {
    e_1.preventDefault();
    e_1.stopPropagation();
    setIsDragOver(false);
    try {
      const dragData = JSON.parse(e_1.dataTransfer.getData("application/reactflow"));

      // Allow actions and logic nodes to be dropped between nodes
      if ((dragData.nodeType === "action" || dragData.nodeType === "logic") && data?.onDropBetween) {
        data.onDropBetween(source, target, dragData);
      }
    } catch (error) {
      console.error("Failed to parse drag data:", error);
    }
  }, [data, source, target]);

  // Determine branch label for if-then-else and wait-resumed/timed_out edges
  const getBranchLabel = () => {
    if (sourceHandle === "source-then") return {
      text: "Then",
      bg: "bg-green-100 dark:bg-green-900/50",
      color: "text-green-700 dark:text-green-300"
    };
    if (sourceHandle === "source-else") return {
      text: "Else",
      bg: "bg-red-100 dark:bg-red-900/50",
      color: "text-red-700 dark:text-red-300"
    };
    if (sourceHandle === "source-resumed") return {
      text: "Resumed",
      bg: "bg-green-100 dark:bg-green-900/50",
      color: "text-green-700 dark:text-green-300"
    };
    if (sourceHandle === "source-timed_out") return {
      text: "Timed Out",
      bg: "bg-amber-100 dark:bg-amber-900/50",
      color: "text-amber-700 dark:text-amber-300"
    };
    if (data?.isOnComplete) return {
      text: "On Complete",
      bg: "bg-amber-100 dark:bg-amber-900/50",
      color: "text-amber-700 dark:text-amber-300"
    };
    return null;
  };
  const branchLabel = getBranchLabel();

  // Get edge styling based on execution status.
  // NOTE: animations are applied via CSS class (edgeClassName) rather than inline style, so
  // the .panning suppression in globals.css works without needing !important on inline styles.
  const isBranchEdge = sourceHandle === "source-then" || sourceHandle === "source-else" || sourceHandle === "source-resumed" || sourceHandle === "source-timed_out";
  const {
    edgeStyle,
    edgeClassName
  } = useMemo(() => {
    const baseStyle = {
      ...style
    };
    const sourceStatus = data?.sourceNodeStatus;
    const targetStatus = data?.targetNodeStatus;
    const hasActiveRun = data?.hasActiveRun;

    // Build mode (not running)
    if (!hasActiveRun) {
      // Dotted for edges to placeholder nodes (Add Node button)
      if (isTargetPlaceholder) {
        return {
          edgeStyle: {
            ...baseStyle,
            stroke: "#3b82f6",
            strokeWidth: 2,
            strokeDasharray: "6,4"
          },
          edgeClassName: ""
        };
      }
      // Animated dashed blue for regular node connections in build mode
      return {
        edgeStyle: {
          ...baseStyle,
          stroke: "#3b82f6",
          strokeWidth: 2,
          strokeDasharray: "8,4"
        },
        edgeClassName: "edge-animated"
      };
    }

    // During run: edges to placeholder stay dotted blue
    if (isTargetPlaceholder) {
      return {
        edgeStyle: {
          ...baseStyle,
          stroke: "#3b82f6",
          strokeWidth: 2,
          strokeDasharray: "6,4"
        },
        edgeClassName: ""
      };
    }

    // For branch edges (if-then-else), check if this branch was actually taken.
    // When the if-node succeeds but the target node is idle/skipped, this branch
    // was NOT taken — show it as idle (gray) instead of inheriting the source's green.
    if (isBranchEdge && sourceStatus === "success" && (targetStatus === "idle" || targetStatus === "skipped")) {
      return {
        edgeStyle: {
          ...baseStyle,
          stroke: "#a1a1aa",
          strokeWidth: 2,
          strokeDasharray: "6,4"
        },
        edgeClassName: ""
      };
    }
    switch (sourceStatus) {
      case "running":
        return {
          edgeStyle: {
            ...baseStyle,
            stroke: "#f97316",
            strokeWidth: 2,
            strokeDasharray: "6,4"
          },
          edgeClassName: "edge-animated edge-animated--fast"
        };
      case "waiting":
        return {
          edgeStyle: {
            ...baseStyle,
            stroke: "#f59e0b",
            strokeWidth: 2,
            strokeDasharray: "6,4"
          },
          edgeClassName: "edge-animated"
        };
      case "success":
        return {
          edgeStyle: {
            ...baseStyle,
            stroke: "#22c55e",
            strokeWidth: 2,
            strokeDasharray: "6,4"
          },
          edgeClassName: "edge-animated edge-animated--medium"
        };
      case "failure":
        return {
          edgeStyle: {
            ...baseStyle,
            stroke: "#ef4444",
            strokeWidth: 2,
            strokeDasharray: "6,4"
          },
          edgeClassName: "edge-animated edge-animated--medium"
        };
      case "idle":
      default:
        // Grey dashed (static) for pending nodes during run
        return {
          edgeStyle: {
            ...baseStyle,
            stroke: "#a1a1aa",
            strokeWidth: 2,
            strokeDasharray: "6,4"
          },
          edgeClassName: ""
        };
    }
  }, [style, data?.sourceNodeStatus, data?.targetNodeStatus, data?.hasActiveRun, isTargetPlaceholder, isBranchEdge]);
  return <>
      <BaseEdge id={id} path={edgePath} style={edgeStyle} className={edgeClassName} markerEnd={markerEnd} />
      <EdgeLabelRenderer>
        {/* Branch label for if-then-else edges */}
        {branchLabel && <div className={`nodrag nopan absolute rounded px-2 py-0.5 text-xs font-medium ${branchLabel.bg} ${branchLabel.color}`} style={{
        transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY + 20}px)`,
        pointerEvents: "none"
      }}>
            {branchLabel.text}
          </div>}

        {/* Plus button for adding nodes between */}
        {!shouldHideButton && <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className="nodrag nopan absolute" style={{
        transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
        pointerEvents: "all"
      }}>
            {isDragOver ?
        // Active drop target (hovering directly over) - more prominent
        <div className="border-3 flex h-10 w-10 scale-110 items-center justify-center rounded-full border-blue-500 bg-blue-100 text-blue-700 shadow-xl ring-4 ring-blue-500/30 transition-all dark:bg-blue-500/20 dark:text-blue-300">
                <IconPlusOutline18 className="h-5 w-5" />
              </div> : isGlobalDragging ?
        // Potential drop zone highlight (dragging action/logic but not hovering)
        <div className="shadow-ds-soft flex h-7 w-7 animate-pulse items-center justify-center rounded-full border-2 border-dashed border-blue-400 bg-blue-50/70 text-blue-500 backdrop-blur-md dark:border-blue-500/70 dark:bg-blue-500/10 dark:text-blue-400">
                <IconPlusOutline18 className="h-3.5 w-3.5" />
              </div> : <Popover open={menuOpen} onOpenChange={open => {
          if (!open) setMenuOpen(false);
        }} modal={false}>
                <PopoverTrigger asChild>
                  <button className="bg-ds-surface-card shadow-ds-soft flex h-7 w-7 items-center justify-center rounded-full border border-blue-400 text-blue-600 ring-1 ring-blue-400/30 transition-all hover:scale-110 hover:border-blue-500 hover:bg-blue-50 hover:shadow-md hover:ring-blue-500/40 dark:border-blue-500 dark:text-blue-400 dark:hover:bg-blue-950" onClick={e_2 => {
              e_2.stopPropagation();
              setMenuOpen(prev => !prev);
            }}>
                    <IconPlusOutline18 className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="border-ds-border-default bg-ds-surface-card w-64 p-2 shadow-xl" align="center" onPointerDownOutside={() => setMenuOpen(false)}>
                  <div className="flex flex-col">
                    <button onClick={handleAddAction} className="flex items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors duration-150 hover:bg-green-50 dark:hover:bg-green-950/20">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-50 dark:bg-green-950/30">
                        <IconBoltOutline18 className="h-4.5 w-4.5 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="flex-1">
                        <div className="body-sm-semibold">Add Action</div>
                        <div className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">
                          Execute a workflow action
                        </div>
                      </div>
                    </button>

                    <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 my-1 h-px" />

                    <button onClick={handleAddLogic} className="flex items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors duration-150 hover:bg-purple-50 dark:hover:bg-purple-950/20">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-50 dark:bg-purple-950/30">
                        <IconCodeBranchOutline18 className="h-4.5 w-4.5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div className="flex-1">
                        <div className="body-sm-semibold">Add Logic Gate</div>
                        <div className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">
                          Control flow conditionally
                        </div>
                      </div>
                    </button>

                    <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 my-1 h-px" />

                    <button onClick={handleAddBlock} className="flex items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors duration-150 hover:bg-orange-50 dark:hover:bg-orange-950/20">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-50 dark:bg-orange-950/30">
                        <IconLayersStackedOutline18 className="h-4.5 w-4.5 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="flex-1">
                        <div className="body-sm-semibold">Add Block</div>
                        <div className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">
                          Insert a reusable block
                        </div>
                      </div>
                    </button>
                  </div>
                </PopoverContent>
              </Popover>}
          </div>}
      </EdgeLabelRenderer>
    </>;
}

// Props type for the edge component
type WorkflowEdgeProps = EdgeProps<WorkflowEdge> & {
  sourceHandle?: string | null;
};

// Custom comparator for React.memo - only re-render when visual properties change
function arePropsEqual(prevProps: WorkflowEdgeProps, nextProps: WorkflowEdgeProps): boolean {
  return prevProps.id === nextProps.id && prevProps.source === nextProps.source && prevProps.target === nextProps.target && prevProps.sourceX === nextProps.sourceX && prevProps.sourceY === nextProps.sourceY && prevProps.targetX === nextProps.targetX && prevProps.targetY === nextProps.targetY && prevProps.sourcePosition === nextProps.sourcePosition && prevProps.targetPosition === nextProps.targetPosition && prevProps.sourceHandle === nextProps.sourceHandle && prevProps.markerEnd === nextProps.markerEnd && prevProps.data?.hideAddButton === nextProps.data?.hideAddButton && prevProps.data?.sourceNodeStatus === nextProps.data?.sourceNodeStatus && prevProps.data?.targetNodeStatus === nextProps.data?.targetNodeStatus && prevProps.data?.hasActiveRun === nextProps.data?.hasActiveRun && prevProps.data?.isDraggingFromLibrary === nextProps.data?.isDraggingFromLibrary && prevProps.data?.isOnComplete === nextProps.data?.isOnComplete && prevProps.data?.isTargetForEach === nextProps.data?.isTargetForEach && !!(prevProps.data?.onAddActionBetween || prevProps.data?.onAddLogicBetween || prevProps.data?.onAddBlockBetween || prevProps.data?.onDropBetween) === !!(nextProps.data?.onAddActionBetween || nextProps.data?.onAddLogicBetween || nextProps.data?.onAddBlockBetween || nextProps.data?.onDropBetween);
}

// React.memo prevents re-renders when props haven't changed
// This is important for React Flow performance with many edges
const WorkflowEdge = memo(WorkflowEdgeComponent, arePropsEqual);
export default WorkflowEdge;